/**
 * Armenian ↔ Latin in two systems: ISO 9985:1996 and ALA-LC (Library of Congress).
 *
 * Sources:
 *
 * - ALA-LC — the Library of Congress "Armenian Romanization Table", 2022 version
 *   (loc.gov/catdir/cpso/romanization/armenian.pdf): the letter table with Eastern/Classical
 *   values and the Western Armenian values in brackets (b↔p, g↔k, d↔t, ts↔dz, ch↔j), the
 *   aspirate sign ʻ (U+02BB), the prime ʹ (U+02B9) placed between two letters that would
 *   otherwise read as a digraph (Note 3), the exceptions for եվ (Note 6), initial y and ḥ in
 *   Classical orthography (Notes 2 and 4), և as ev (ew in Classical orthography), and the
 *   punctuation rules (verjaket → full stop; question and exclamation marks moved to the end
 *   of the sentence; the shesht not romanized; « » as quotation marks).
 * - ISO 9985:1996 — "Transliteration of Armenian characters into Latin characters". The
 *   standard is paid; the values were read from the comparison tables published by the Estonian
 *   Institute of the Language (T. T. Pedersen, "Armenian", rev. 1.3, 2004) and the Wikipedia
 *   article "Romanization of Armenian". ISO 9985 uses no digraphs, so it can be reversed
 *   exactly: ու is ow, և is ew, ը is ë, օ is ò, aspirates are written with ʼ (U+02BC) except չ,
 *   which is č, with ճ as č̣.
 *
 * What a program cannot do: the "stem" and "morpheme" conditions of the ALA-LC notes (initial ḥ
 * and y apply to the start of a word or stem; the եվ exceptions are a word list), so only the
 * start of a word is used here. Reading ALA-LC back to Armenian is a best guess: ts, dz, ch and
 * j depend on whether Eastern or Western values are meant, and e, y, o can be read more than one
 * way.
 */

export type ArmenianSystem = "iso" | "ala-lc";

export interface ArmenianOptions {
  /** ALA-LC: use the Western Armenian values for b/p, g/k, d/t, ts/dz and ch/j. */
  western: boolean;
  /** ALA-LC: Classical orthography (initial ե → y before a vowel, initial յ → ḥ, և → ew). */
  classical: boolean;
}

export interface ArmenianResult {
  text: string;
  /** Latin letters or other characters that were not recognised (Latin → Armenian only). */
  unknown: string[];
}

const ISO_ASPIRATE = "ʼ";
const LC_ASPIRATE = "ʻ";
const PRIME = "ʹ";

/** Lower-case Armenian letter, ISO 9985, ALA-LC Eastern/Classical, ALA-LC Western. */
const LETTERS: readonly (readonly [string, string, string, string])[] = [
  ["ա", "a", "a", "a"],
  ["բ", "b", "b", "p"],
  ["գ", "g", "g", "k"],
  ["դ", "d", "d", "t"],
  ["ե", "e", "e", "e"],
  ["զ", "z", "z", "z"],
  ["է", "ē", "ē", "ē"],
  ["ը", "ë", "ě", "ě"],
  ["թ", `t${ISO_ASPIRATE}`, `t${LC_ASPIRATE}`, `t${LC_ASPIRATE}`],
  ["ժ", "ž", "zh", "zh"],
  ["ի", "i", "i", "i"],
  ["լ", "l", "l", "l"],
  ["խ", "x", "kh", "kh"],
  ["ծ", "ç", "ts", "dz"],
  ["կ", "k", "k", "g"],
  ["հ", "h", "h", "h"],
  ["ձ", "j", "dz", "ts"],
  ["ղ", "ġ", "gh", "gh"],
  ["ճ", "č̣", "ch", "j"],
  ["մ", "m", "m", "m"],
  ["յ", "y", "y", "y"],
  ["ն", "n", "n", "n"],
  ["շ", "š", "sh", "sh"],
  ["ո", "o", "o", "o"],
  ["չ", "č", `ch${LC_ASPIRATE}`, `ch${LC_ASPIRATE}`],
  ["պ", "p", "p", "b"],
  ["ջ", "ǰ", "j", "ch"],
  ["ռ", "ṙ", "ṛ", "ṛ"],
  ["ս", "s", "s", "s"],
  ["վ", "v", "v", "v"],
  ["տ", "t", "t", "d"],
  ["ր", "r", "r", "r"],
  ["ց", `c${ISO_ASPIRATE}`, `ts${LC_ASPIRATE}`, `ts${LC_ASPIRATE}`],
  ["ւ", "w", "w", "w"],
  ["փ", `p${ISO_ASPIRATE}`, `p${LC_ASPIRATE}`, `p${LC_ASPIRATE}`],
  ["ք", `k${ISO_ASPIRATE}`, `k${LC_ASPIRATE}`, `k${LC_ASPIRATE}`],
  ["օ", "ò", "ō", "ō"],
  ["ֆ", "f", "f", "f"],
];

const DIGRAPH_U: readonly [string, string, string] = ["ow", "u", "u"];
const LIGATURE_EV: readonly [string, string, string] = ["ew", "ev", "ev"];

const LIGATURES: Record<string, string> = {
  ﬓ: "մն",
  ﬔ: "մե",
  ﬕ: "մի",
  ﬖ: "վն",
  ﬗ: "մխ",
};
const LIGATURE_PATTERN = /[ﬓ-ﬗ]/g;

const PUNCTUATION: Record<string, string> = {
  "՝": ",",
  "֊": "-",
  "՚": "’",
  "«": "“",
  "»": "”",
};
const DROPPED = new Set(["՛", "՟"]);
const QUESTION = "՞";
const EXCLAMATION = "՜";
const VERJAKET = "։";

const ARMENIAN_LOWER_START = 0x5_61;
const ARMENIAN_LOWER_END = 0x5_87;
const ARMENIAN_UPPER_START = 0x5_31;
const ARMENIAN_UPPER_END = 0x5_56;
const CASE_OFFSET = 0x30;
const LIGATURE_ARMENIAN = "և";

const VOWELS = new Set([..."աեէըիոօ"]);

function column(system: ArmenianSystem, options: ArmenianOptions): 1 | 2 | 3 {
  if (system === "iso") {
    return 1;
  }
  return options.western ? 3 : 2;
}

const FORWARD = new Map<string, readonly [string, string, string]>();
for (const [arm, iso, east, west] of LETTERS) {
  FORWARD.set(arm, [iso, east, west]);
}

function isUpperArmenian(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  return code >= ARMENIAN_UPPER_START && code <= ARMENIAN_UPPER_END;
}

function isArmenian(ch: string | undefined): boolean {
  if (ch === undefined) {
    return false;
  }
  const code = ch.codePointAt(0) ?? 0;
  return (
    (code >= ARMENIAN_UPPER_START && code <= ARMENIAN_UPPER_END) ||
    (code >= ARMENIAN_LOWER_START && code <= ARMENIAN_LOWER_END)
  );
}

function lowerArmenian(ch: string): string {
  if (isUpperArmenian(ch)) {
    return String.fromCodePoint((ch.codePointAt(0) ?? 0) + CASE_OFFSET);
  }
  return ch;
}

function capitalizeFirst(text: string): string {
  const first = [...text][0] ?? "";
  return first.toUpperCase() + text.slice(first.length);
}

// ---------------------------------------------------------------------------------------
// Armenian → Latin
// ---------------------------------------------------------------------------------------

const DIGRAPHS = new Set(["zh", "kh", "ts", "dz", "gh", "ch", "sh"]);

/** True when writing `next` right after `previous` would spell another letter's digraph. */
function needsPrime(previous: string, next: string): boolean {
  if (previous.length !== 1) {
    return false;
  }
  const pair = (previous + (next[0] ?? "")).toLowerCase();
  return DIGRAPHS.has(pair);
}

interface Context {
  system: ArmenianSystem;
  options: ArmenianOptions;
  col: 1 | 2 | 3;
}

function letterRomanization(
  chars: readonly string[],
  i: number,
  ctx: Context,
  wordStart: boolean
): { text: string; used: number } | undefined {
  const lower = lowerArmenian(chars[i] as string);
  const next = lowerArmenian(chars[i + 1] ?? "");
  if (lower === "ո" && next === "ւ") {
    return { text: DIGRAPH_U[ctx.col - 1] as string, used: 2 };
  }
  if (lower === LIGATURE_ARMENIAN) {
    const classical = ctx.system === "ala-lc" && ctx.options.classical;
    return {
      text: classical ? "ew" : (LIGATURE_EV[ctx.col - 1] as string),
      used: 1,
    };
  }
  const entry = FORWARD.get(lower);
  if (!entry) {
    return;
  }
  if (ctx.system === "ala-lc" && ctx.options.classical && wordStart) {
    if (lower === "ե" && VOWELS.has(next)) {
      return { text: "y", used: 1 };
    }
    if (lower === "յ") {
      return { text: "ḥ", used: 1 };
    }
  }
  if (ctx.system === "ala-lc" && lower === "ե" && next === "վ") {
    const bothCapital =
      isUpperArmenian(chars[i] as string) &&
      isUpperArmenian(chars[i + 1] as string);
    return { text: bothCapital ? "e" : `e${PRIME}`, used: 1 };
  }
  return { text: entry[ctx.col - 1] as string, used: 1 };
}

function casedText(
  text: string,
  chars: readonly string[],
  i: number,
  used: number
): string {
  const first = chars[i] as string;
  if (!isUpperArmenian(first)) {
    return text;
  }
  const neighbours = [chars[i - 1], chars[i + used]];
  const allCaps = neighbours.some(
    (ch) => ch !== undefined && isUpperArmenian(ch)
  );
  if (allCaps && text.length > 1 && used === 1 && !text.includes(PRIME)) {
    return text.toUpperCase();
  }
  return capitalizeFirst(text);
}

interface ForwardState {
  out: string;
  /** Romanization of the previous letter, for the prime rule; empty after anything else. */
  previous: string;
  pending: "" | "?" | "!";
}

function endSentence(state: ForwardState, mark: string): void {
  state.out += state.pending || mark;
  state.pending = "";
  state.previous = "";
}

function forwardLetter(
  state: ForwardState,
  chars: readonly string[],
  i: number,
  ctx: Context
): number | undefined {
  const wordStart = !isArmenian(chars[i - 1]);
  const hit = letterRomanization(chars, i, ctx, wordStart);
  if (!hit) {
    return;
  }
  const text = casedText(hit.text, chars, i, hit.used);
  if (ctx.system === "ala-lc" && needsPrime(state.previous, text)) {
    state.out += PRIME;
  }
  state.out += text;
  state.previous = text.endsWith(LC_ASPIRATE) || text.length > 1 ? "" : text;
  return hit.used;
}

/** Romanize Armenian-script text; anything that is not Armenian passes through. */
export function romanizeArmenian(
  input: string,
  system: ArmenianSystem,
  options: ArmenianOptions
): ArmenianResult {
  const text = input
    .normalize("NFC")
    .replace(LIGATURE_PATTERN, (c) => LIGATURES[c] ?? c);
  const chars = [...text];
  const ctx: Context = { system, options, col: column(system, options) };
  const state: ForwardState = { out: "", previous: "", pending: "" };
  let i = 0;
  while (i < chars.length) {
    const ch = chars[i] as string;
    const used = forwardLetter(state, chars, i, ctx);
    if (used !== undefined) {
      i += used;
      continue;
    }
    state.previous = "";
    i += 1;
    if (ch === QUESTION) {
      state.pending = "?";
    } else if (ch === EXCLAMATION) {
      state.pending ||= "!";
    } else if (ch === VERJAKET) {
      endSentence(state, ".");
    } else if (!DROPPED.has(ch)) {
      state.out += PUNCTUATION[ch] ?? ch;
    }
  }
  if (state.pending) {
    state.out += state.pending;
  }
  return { text: state.out.normalize("NFC"), unknown: [] };
}

// ---------------------------------------------------------------------------------------
// Latin → Armenian
// ---------------------------------------------------------------------------------------

interface Reading {
  arm: string;
  /** Armenian for a capital Latin letter. */
  upper: string;
}

const MAX_KEY = 4;
const ISO_MARKS = /[ʼ’'ʻʿ‘`]/g;
const LC_MARKS = /[ʻʿ‘`]/g;
const PRIME_VARIANTS = /[ʹ']/g;
const LETTER = /\p{L}/u;

function capitalOf(arm: string, allCaps: boolean): string {
  if (arm === LIGATURE_ARMENIAN) {
    return allCaps ? "ԵՎ" : "Եվ";
  }
  if (arm === "ու") {
    return allCaps ? "ՈՒ" : "Ու";
  }
  if (arm.length > 1 && !allCaps) {
    return capitalizeFirst(arm);
  }
  return arm.toUpperCase();
}

function buildReverse(
  system: ArmenianSystem,
  options: ArmenianOptions
): Map<string, Reading> {
  const table = new Map<string, Reading>();
  const col = column(system, options);
  const put = (latin: string, arm: string) => {
    table.set(latin.normalize("NFC"), { arm, upper: capitalOf(arm, false) });
  };
  for (const row of LETTERS) {
    put(row[col] as string, row[0]);
  }
  put(DIGRAPH_U[col - 1] as string, "ու");
  if (system === "iso") {
    put("ew", LIGATURE_ARMENIAN);
  } else {
    put("ev", LIGATURE_ARMENIAN);
    put("ew", "եւ");
    put("ḥ", "յ");
  }
  return table;
}

function normalizeMarks(text: string, system: ArmenianSystem): string {
  if (system === "iso") {
    return text.replace(ISO_MARKS, ISO_ASPIRATE);
  }
  return text.replace(LC_MARKS, LC_ASPIRATE).replace(PRIME_VARIANTS, PRIME);
}

function matchLatin(
  chars: readonly string[],
  at: number,
  table: ReadonlyMap<string, Reading>
): { reading: Reading; length: number } | undefined {
  for (
    let length = Math.min(MAX_KEY, chars.length - at);
    length > 0;
    length -= 1
  ) {
    const key = chars
      .slice(at, at + length)
      .join("")
      .toLowerCase();
    const reading = table.get(key);
    if (reading) {
      return { reading, length };
    }
  }
  return;
}

const REVERSE_PUNCTUATION: Record<string, string> = {
  "“": "«",
  "”": "»",
};

/** Convert ISO 9985 or ALA-LC Latin text to Armenian. */
export function latinToArmenian(
  input: string,
  system: ArmenianSystem,
  options: ArmenianOptions
): ArmenianResult {
  const table = buildReverse(system, options);
  const chars = [...normalizeMarks(input.normalize("NFC"), system)];
  const unknown = new Set<string>();
  let out = "";
  let i = 0;
  while (i < chars.length) {
    if (system === "ala-lc" && chars[i] === PRIME) {
      i += 1;
      continue;
    }
    const hit = matchLatin(chars, i, table);
    if (hit) {
      const slice = chars.slice(i, i + hit.length).join("");
      const capital = slice !== slice.toLowerCase();
      const allCaps = slice.length > 1 && slice === slice.toUpperCase();
      out += capital ? capitalOf(hit.reading.arm, allCaps) : hit.reading.arm;
      i += hit.length;
      continue;
    }
    const ch = chars[i] as string;
    if (LETTER.test(ch)) {
      unknown.add(ch);
    }
    out += REVERSE_PUNCTUATION[ch] ?? ch;
    i += 1;
  }
  return { text: out.normalize("NFC"), unknown: [...unknown] };
}

// ---------------------------------------------------------------------------------------
// Letter table
// ---------------------------------------------------------------------------------------

export interface ArmenianLetterRow {
  armenian: string;
  iso: string;
  east: string;
  west: string;
}

/** The letter table: capital and small letter, ISO 9985, ALA-LC Eastern and Western. */
export function armenianLetterRows(): ArmenianLetterRow[] {
  const rows: ArmenianLetterRow[] = LETTERS.map(([arm, iso, east, west]) => ({
    armenian: `${arm.toUpperCase()} ${arm}`,
    iso,
    east,
    west: west === east ? "" : west,
  }));
  rows.push(
    {
      armenian: "Ու ու",
      iso: DIGRAPH_U[0],
      east: DIGRAPH_U[1],
      west: "",
    },
    {
      armenian: "և",
      iso: LIGATURE_EV[0],
      east: LIGATURE_EV[1],
      west: "",
    }
  );
  return rows;
}

export const ARMENIAN_SYSTEMS: readonly ArmenianSystem[] = ["iso", "ala-lc"];
